import React, { useState, useEffect } from 'react';
import { ServiceRequest } from './types';
import { useServiceRequests } from './hooks/useServiceRequests';
import { Card, CardContent } from "@/components/ui/card";
import RequestList from './components/RequestList';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TimeSlotView from './TimeSlotView';
import { Button } from "@/components/ui/button";
import { CalendarDays, CalendarRange, Calendar as CalendarIcon, PlusCircle, Loader2, Check, X, CreditCard, Bell, RefreshCw, Share, Ban, AlertCircle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { addDays, startOfWeek, endOfWeek, format, isBefore, addMinutes, parseISO, isToday, isTomorrow } from 'date-fns';
import { enUS, fr } from 'date-fns/locale';
import { Calendar } from "@/components/ui/calendar";
import { supabase } from '@/integrations/supabase/client';
import ServiceCheckoutDialog from '@/components/service-checkout/ServiceCheckoutDialog';
import { formatCurrency } from "@/utils/analyticsUtils";
import { Badge } from "@/components/ui/badge";
import { generateServiceRequestShareableLink } from './utils/requestUtils';
import { toast } from "@/components/ui/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useTranslation } from "react-i18next";

interface ServiceRequestsViewProps {
  userId?: string;
  initialFilter?: 'all' | 'pending' | 'scheduled' | 'completed' | 'calendar' | 'upcoming' | 'rejected' | 'create';
  viewMode?: 'list' | 'detail';
}

interface Service {
  id: string;
  name: string;
  price: number;
  image_url?: string;
  user_id: string;
  service_mode?: string;
  travel_fee?: number;
  currency?: string;
  duration?: number;
  duration_unit?: string;
  service_area?: string;
  requires_scheduling?: boolean;
  is_active: boolean;
}

interface ServiceImage {
  id: string;
  service_id: string;
  image_url: string;
  created_at: string;
}

const ServiceRequestsView: React.FC<ServiceRequestsViewProps> = ({ userId, initialFilter = 'pending', viewMode = 'list' }) => {
  const { t, i18n } = useTranslation();
  const { 
    requests, 
    loading, 
    fetchRequests, 
    sendReminderToCustomer, 
    rescheduleServiceRequest,
    handleAcceptRequest,
    handleRejectRequest,
    handleCompleteRequest,
    handleMarkAsPaid
  } = useServiceRequests(userId);
  
  const [activeView, setActiveView] = useState<string>(initialFilter);
  const [calendarView, setCalendarView] = useState<'day' | 'week' | 'month'>('day');
  const location = useLocation();
  const navigate = useNavigate();
  const { requestId } = useParams<{ requestId: string }>();
  const [currentUserId, setCurrentUserId] = useState<string | null>(userId || null);
  const [services, setServices] = useState<Service[]>([]);
  const [serviceImages, setServiceImages] = useState<{[serviceId: string]: ServiceImage[]}>({});
  const [selectedServiceIndex, setSelectedServiceIndex] = useState<{[serviceId: string]: number}>({});
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [isServiceCheckoutOpen, setIsServiceCheckoutOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [userCurrency, setUserCurrency] = useState<string>('KES');
  const [isProcessing, setIsProcessing] = useState<{ [key: string]: boolean }>({});

  // Initialize to today's date and find first available slot
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [firstAvailableSlotFound, setFirstAvailableSlotFound] = useState(false);
  
  console.log("ViewMode:", viewMode, "RequestId:", requestId);
  
  // Find the specific request if in detail view mode
  const currentRequest = requestId ? requests.find(r => r.id === requestId) : null;
  
  // Log request data to diagnose any issues
  useEffect(() => {
    if (viewMode === 'detail' && requestId) {
      console.log("Looking for request with ID:", requestId);
      console.log("Available requests:", requests.length);
      console.log("Current request found:", currentRequest ? "Yes" : "No");
    }
  }, [viewMode, requestId, requests, currentRequest]);
  
  // Make sure to fetch requests when in detail view
  useEffect(() => {
    if (viewMode === 'detail' && requestId) {
      // Only fetch if we don't already have the request
      const requestExists = requests.some(req => req.id === requestId);
      if (!requestExists && !loading) {
        fetchRequests();
      }
    }
  }, [viewMode, requestId, requests.length, loading]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');
    if (viewParam && viewMode === 'list') {
      setActiveView(viewParam);
    } else {
      setActiveView(initialFilter);
    }
  }, [location.search, initialFilter, viewMode]);

  // Ensure we have the current user ID if not provided
  useEffect(() => {
    if (!currentUserId) {
      const fetchCurrentUser = async () => {
        try {
          const { data } = await supabase.auth.getUser();
          if (data?.user) {
            console.log('Fetched user ID for create request form:', data.user.id);
            setCurrentUserId(data.user.id);
            
            // Also fetch the user's profile to get currency
            fetchUserProfile(data.user.id);
          }
        } catch (error) {
          console.error('Error fetching current user for create request form:', error);
        }
      };
      
      fetchCurrentUser();
    } else {
      // If we already have the user ID, fetch their profile
      fetchUserProfile(currentUserId);
    }
  }, [currentUserId]);
  
  // Fetch user profile to get currency
  const fetchUserProfile = async (uid: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('currency')
        .eq('id', uid)
        .single();
        
      if (error) throw error;
      
      if (data && data.currency) {
        setUserCurrency(data.currency);
      }
    } catch (error) {
      console.error('Error fetching user profile currency:', error);
    }
  };

  // Fetch services when in create mode
  useEffect(() => {
    if (activeView === 'create' && currentUserId) {
      fetchServices();
    }
  }, [activeView, currentUserId]);

  // Find the first available slot when requests change
  useEffect(() => {
    if (requests.length > 0 && !firstAvailableSlotFound) {
      findFirstAvailableSlot();
    }
  }, [requests, firstAvailableSlotFound]);

  const fetchServices = async () => {
    if (!currentUserId) return;
    
    setIsLoadingServices(true);
    try {
      // Fetch all active services for this user
      const { data: servicesData, error: servicesError } = await supabase
        .from('services')
        .select('*')
        .eq('user_id', currentUserId)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
        
      if (servicesError) throw servicesError;
      
      // Fetch images for each service
      const serviceIds = servicesData?.map(service => service.id) || [];
      const { data: imagesData, error: imagesError } = await supabase
        .from('service_images')
        .select('*')
        .in('service_id', serviceIds);
        
      if (imagesError) throw imagesError;
      
      // Organize images by service ID
      const imagesByService: {[serviceId: string]: ServiceImage[]} = {};
      imagesData?.forEach(image => {
        if (!imagesByService[image.service_id]) {
          imagesByService[image.service_id] = [];
        }
        imagesByService[image.service_id].push(image);
      });
      
      // Initialize selected image index for each service
      const initialIndexes: {[serviceId: string]: number} = {};
      serviceIds.forEach(id => {
        initialIndexes[id] = 0;
      });
      
      setServices(servicesData || []);
      setServiceImages(imagesByService);
      setSelectedServiceIndex(initialIndexes);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setIsLoadingServices(false);
    }
  };

  const handleImageNavigation = (serviceId: string, direction: 'prev' | 'next') => {
    const images = serviceImages[serviceId] || [];
    if (images.length <= 1) return;
    
    setSelectedServiceIndex(prev => {
      const currentIndex = prev[serviceId] || 0;
      let newIndex;
      
      if (direction === 'next') {
        newIndex = (currentIndex + 1) % images.length;
      } else {
        newIndex = (currentIndex - 1 + images.length) % images.length;
      }
      
      return { ...prev, [serviceId]: newIndex };
    });
  };

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setIsServiceCheckoutOpen(true);
  };

  const handleCheckoutClose = () => {
    setIsServiceCheckoutOpen(false);
    setSelectedService(null);
  };

  const handleCheckoutSuccess = () => {
    setIsServiceCheckoutOpen(false);
    setSelectedService(null);
    fetchRequests();
    handleFilterChange('pending');
  };

  const findFirstAvailableSlot = () => {
    // Start with today
    let checkDate = new Date();
    
    // Check up to 14 days in the future
    for (let i = 0; i < 14; i++) {
      const dateRequests = requests.filter(r => {
        if (!r.scheduled_at) return false;
        
        const requestDate = new Date(r.scheduled_at);
        return requestDate.getDate() === checkDate.getDate() && 
               requestDate.getMonth() === checkDate.getMonth() && 
               requestDate.getFullYear() === checkDate.getFullYear();
      });

      // Check if this day has available slots
      const hours = generateHourSlots(checkDate);
      const hasAvailableSlot = hours.some(hour => {
        // Check if hour is in the future
        if (isBefore(hour, new Date())) return false;
        
        // Check if hour overlaps with any request
        return !isTimeSlotOccupied(hour, dateRequests);
      });

      if (hasAvailableSlot) {
        setSelectedDate(checkDate);
        setFirstAvailableSlotFound(true);
        return;
      }

      // Move to next day
      checkDate = addDays(checkDate, 1);
    }

    // If no available slot found in next 14 days, just use tomorrow
    setSelectedDate(addDays(new Date(), 1));
    setFirstAvailableSlotFound(true);
  };

  // Helper function to generate hour slots for a day
  const generateHourSlots = (date: Date) => {
    const slots = [];
    const startHour = date.getHours() < 6 ? 6 : date.getHours();
    
    // For today, start from current hour, for other days start from 6 AM
    const isToday = date.getDate() === new Date().getDate() && 
                    date.getMonth() === new Date().getMonth() && 
                    date.getFullYear() === new Date().getFullYear();
                    
    const hourStart = isToday ? startHour : 6;
    
    // Generate slots at the top of the hour and half-hour
    for (let i = hourStart; i <= 22; i++) {
      // Top of the hour
      const hourSlot = new Date(date);
      hourSlot.setHours(i, 0, 0, 0);
      slots.push(hourSlot);
      
      // Half hour (except for the last hour at 22:00/10PM)
      if (i < 22) {
        const halfHourSlot = new Date(date);
        halfHourSlot.setHours(i, 30, 0, 0);
        slots.push(halfHourSlot);
      }
    }
    
    return slots;
  };

  // Helper function to check if a time slot is occupied
  const isTimeSlotOccupied = (hour: Date, dayRequests: ServiceRequest[]): boolean => {
    if (!dayRequests || dayRequests.length === 0) return false;
    
    const hourStart = hour;
    const hourEnd = addMinutes(hour, 59);
    
    return dayRequests.some(request => {
      if (!request.scheduled_at) return false;
      
      const requestTime = new Date(request.scheduled_at);
      const requestDuration = request.services?.duration || 60;
      const requestDurationMinutes = request.services?.duration_unit === 'hours' 
        ? requestDuration * 60 
        : requestDuration;
      const requestEnd = addMinutes(requestTime, requestDurationMinutes);
      
      return (
        (requestTime >= hourStart && requestTime <= hourEnd) ||
        (requestEnd > hourStart && requestEnd <= hourEnd) ||
        (requestTime < hourStart && requestEnd > hourEnd)
      );
    });
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const acceptedRequests = requests.filter(r => r.status === 'accepted');
  const completedRequests = requests.filter(r => r.status === 'completed');
  const rejectedRequests = requests.filter(r => r.status === 'rejected');
  
  const upcomingRequests = requests.filter(r => {
    if (r.status !== 'accepted') return false;
    if (!r.scheduled_at) return false;
    const scheduledDate = new Date(r.scheduled_at);
    const now = new Date();
    return scheduledDate > now;
  }).sort((a, b) => {
    const dateA = a.scheduled_at ? new Date(a.scheduled_at).getTime() : 0;
    const dateB = b.scheduled_at ? new Date(b.scheduled_at).getTime() : 0;
    return dateA - dateB;
  });

  const handleSendReminder = async (requestId: string): Promise<{ success: boolean, error?: string }> => {
    const result = await sendReminderToCustomer(requestId);
    return result;
  };

  const handleReschedule = async (requestId: string, newDate: Date) => {
    await rescheduleServiceRequest(requestId, newDate);
  };

  const handleFilterChange = (filter: string) => {
    if (viewMode === 'detail') {
      // If in detail view, navigate back to the list with the selected filter
      navigate(`/dashboard/service-requests?view=${filter}`);
      return;
    }
    
    const params = new URLSearchParams(location.search);
    params.set('view', filter);
    navigate(`${location.pathname}?${params.toString()}`);
    setActiveView(filter);
  };

  const getWeekDates = () => {
    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(addDays(start, i));
    }
    return dates;
  };

  const today = new Date();

  const handleDateChange = (newDate: Date) => {
    setSelectedDate(newDate);
  };

  const renderCalendarOptions = () => {
    if (activeView !== 'calendar') return null;

    return (
      <div className="mb-6">
        <Tabs 
          defaultValue={calendarView} 
          onValueChange={(value) => setCalendarView(value as 'day' | 'week' | 'month')}
          className="w-full"
        >
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="day" className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4" />
              <span>{t('navigation.day')}</span>
            </TabsTrigger>
            <TabsTrigger value="week" className="flex items-center gap-2">
              <CalendarRange className="h-4 w-4" />
              <span>{t('navigation.week')}</span>
            </TabsTrigger>
            <TabsTrigger value="month" className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              <span>{t('navigation.month')}</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
    );
  };

  // Add helper function for formatting dates in the detailed view
  const formatRequestDate = (date: string) => {
    const parsedDate = parseISO(date);
    if (isToday(parsedDate)) {
      return t('navigation.today');
    } else if (isTomorrow(parsedDate)) {
      return t('navigation.tomorrow');
    } else {
      // Use different format based on language
      if (i18n.language === 'fr') {
        return formatDateWithLocale(parsedDate, 'd MMMM yyyy');
      } else {
        return formatDateWithLocale(parsedDate, 'MMM d, yyyy');
      }
    }
  };

  // Add helper function for formatting date and time
  const formatRequestDateTime = (date: string) => {
    const parsedDate = parseISO(date);
    if (isToday(parsedDate)) {
      return `${t('navigation.today')}, ${format(parsedDate, 'HH:mm', { locale: getDateFnsLocale() })}`;
    } else if (isTomorrow(parsedDate)) {
      return `${t('navigation.tomorrow')}, ${format(parsedDate, 'HH:mm', { locale: getDateFnsLocale() })}`;
    } else {
      // Use different format based on language
      if (i18n.language === 'fr') {
        return format(parsedDate, 'd MMMM yyyy, HH:mm', { locale: getDateFnsLocale() });
      } else {
        return format(parsedDate, 'MMM d, yyyy, h:mm a', { locale: getDateFnsLocale() });
      }
    }
  };

  // Add helper function for status badges in the detailed view
  const getStatusBadge = (status: string) => {
    const statusStyles = {
      pending: "bg-yellow-100 text-yellow-800",
      accepted: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      completed: "bg-blue-100 text-blue-800",
      cancelled: "bg-red-600 text-white font-bold px-3 py-1 rounded shadow",
    };

    return (
      <Badge className={statusStyles[status as keyof typeof statusStyles]}>
        {status.toUpperCase()}
      </Badge>
    );
  };

  // Add wrapper functions for handling actions that need processing state
  const handleDetailAccept = async (requestId: string) => {
    try {
      setIsProcessing(prev => ({ ...prev, [requestId]: true }));
      await handleAcceptRequest(requestId);
    } finally {
      setIsProcessing(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDetailReject = async (requestId: string) => {
    try {
      setIsProcessing(prev => ({ ...prev, [requestId]: true }));
      await handleRejectRequest(requestId);
    } finally {
      setIsProcessing(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDetailComplete = async (requestId: string) => {
    try {
      setIsProcessing(prev => ({ ...prev, [requestId]: true }));
      await handleCompleteRequest(requestId);
    } finally {
      setIsProcessing(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDetailMarkAsPaid = async (requestId: string) => {
    try {
      setIsProcessing(prev => ({ ...prev, [requestId]: true }));
      await handleMarkAsPaid(requestId);
    } finally {
      setIsProcessing(prev => ({ ...prev, [requestId]: false }));
    }
  };

  const handleDetailSendReminder = async (requestId: string) => {
    try {
      setIsProcessing(prev => ({ ...prev, [requestId]: true }));
      await sendReminderToCustomer(requestId);
    } finally {
      setIsProcessing(prev => ({ ...prev, [requestId]: false }));
    }
  };

  // Update the getCustomerShareableLink function to use the customer_page_link
  const getCustomerShareableLink = (request: ServiceRequest) => {
    if (!request.customer_page_link) return null;
    
    const baseUrl = window.location.origin;
    return `${baseUrl}${request.customer_page_link}`;
  };

  // Update the handleShareWithCustomer function to handle cases where the link needs to be generated
  const handleShareWithCustomer = async (requestId: string) => {
    setIsProcessing(prev => ({ ...prev, [requestId]: true }));
    
    try {
      // If the request doesn't exist in our state, can't proceed
      if (!currentRequest) {
        console.error("Current request not found in state");
        toast({
          variant: "destructive",
          title: "Error",
          description: "Unable to generate sharing link. Please try again."
        });
        return;
      }
      
      // Check if we already have a customer page link
      let shareableLink = currentRequest.customer_page_link
        ? getCustomerShareableLink(currentRequest)
        : null;
      
      // If no link exists, generate one
      if (!shareableLink) {
        // Generate a unique ID for the link
        const randomId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 15);
        const newLink = `/service-request/${requestId}/${randomId}`;
        
        // Update the database with the new link
        const { error } = await supabase
          .from('service_requests')
          .update({
            customer_page_link: newLink
          })
          .eq('id', requestId);
        
        if (error) {
          console.error("Error generating customer page link:", error);
          toast({
            variant: "destructive",
            title: "Error",
            description: "Failed to generate sharing link. Please try again."
          });
          return;
        }
        
        // Re-fetch the requests to update our state
        fetchRequests();
        
        // Create the full shareable link
        const baseUrl = window.location.origin;
        shareableLink = `${baseUrl}${newLink}`;
      }
      
      // Copy the link to clipboard
      await navigator.clipboard.writeText(shareableLink);
      
      toast({
        title: "Link Copied!",
        description: "A secure customer link has been copied to your clipboard.",
      });
    } catch (error) {
      console.error("Error sharing with customer:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate sharing link. Please try again."
      });
    } finally {
      setIsProcessing(prev => ({ ...prev, [requestId]: false }));
    }
  };

  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [customCancelReason, setCustomCancelReason] = useState("");
  const [cancelProcessing, setCancelProcessing] = useState(false);
  const [cancelConfirmStep, setCancelConfirmStep] = useState(false);
  // Typical provider-side reasons
  const providerQuickReasons = [
    t('cancelDialog.customerNoShow'),
    t('cancelDialog.customerCancelled'),
    t('cancelDialog.doubleBooking'),
    t('cancelDialog.safetyConcerns'),
    t('cancelDialog.emergency'),
    t('cancelDialog.other')
  ];
  // Use provider reasons for admin side
  const quickReasons = providerQuickReasons;

  const handleCancelRequest = async () => {
    if (!cancelReason && !customCancelReason) {
      toast({
        variant: "destructive",
        title: t('reasonRequired'),
        description: t('reasonRequiredDescription')
      });
      return;
    }
    if (!cancelConfirmStep) {
      setCancelConfirmStep(true);
      return;
    }
    try {
      setCancelProcessing(true);
      const reason = cancelReason === t('cancelDialog.other') ? customCancelReason : cancelReason;
      const { error } = await supabase
        .from('service_requests')
        .update({ status: 'cancelled', cancellation_reason: reason })
        .eq('id', currentRequest.id);
      if (error) throw error;
      toast({ title: t('requestCancelled'), description: t('requestCancelledDescription') });
      setCancelDialogOpen(false);
      setCancelReason("");
      setCustomCancelReason("");
      setCancelConfirmStep(false);
      if (fetchRequests) fetchRequests();
    } catch (error) {
      toast({ variant: "destructive", title: "Error", description: t('failedToCancelRequest') });
    } finally {
      setCancelProcessing(false);
    }
  };

  // Get the appropriate locale for date-fns based on current language
  const getDateFnsLocale = () => {
    switch (i18n.language) {
      case 'fr':
        return fr;
      case 'en':
      default:
        return enUS;
    }
  };

  // Format date with proper French capitalization rules
  const formatDateWithLocale = (date: Date, formatString: string) => {
    const formatted = format(date, formatString, { locale: getDateFnsLocale() });
    
    // In French, days and months should be lowercase unless at start of sentence
    if (i18n.language === 'fr') {
      return formatted.toLowerCase();
    }
    
    return formatted;
  };

  const renderContent = () => {
    // If in detail view mode, show the service detail
    if (viewMode === 'detail' && requestId) {
      if (!currentRequest) {
        return (
          <div className="p-6 text-center space-y-4">
            <h3 className="text-lg font-medium">{t('requestActions.requestNotFound')}</h3>
            <p className="text-muted-foreground">
              {t('requestActions.requestNotFoundDescription')}
            </p>
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard/service-requests')}
            >
              {t('requestActions.backToRequests')}
            </Button>
          </div>
        );
      }

      // Get the customer page link
      const customerShareableLink = currentRequest.customer_page_link 
        ? getCustomerShareableLink(currentRequest) 
        : null;

      return (
        <>
          {/* Service header with title and status */}
          <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-5 py-4 border-b">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-bold text-gray-800">{currentRequest.services.name}</h2>
              {getStatusBadge(currentRequest.status)}
            </div>
            <p className="text-sm text-gray-600">
              {currentRequest.scheduled_at ? 
                `${t('navigation.requestedOn')} ${formatRequestDate(currentRequest.created_at)}` : 
                `${t('navigation.requestedOn')} ${formatRequestDate(currentRequest.created_at)}`}
            </p>
            {currentRequest.scheduled_at && (
              <p className="text-sm font-medium text-amber-700 mt-1">
                {t('navigation.serviceOn')} {formatRequestDateTime(currentRequest.scheduled_at)}
              </p>
            )}
            {/* Show cancellation reason if cancelled */}
            {currentRequest.status === 'cancelled' && currentRequest.cancellation_reason && (
              <div className="mt-4 bg-red-50 border border-red-200 rounded p-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                <div>
                  <span className="font-semibold text-red-700">{t('navigation.cancelled')}:</span>
                  <span className="text-red-700 ml-2">{currentRequest.cancellation_reason}</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Content sections */}
          <div className="p-5 space-y-5">
            {/* Customer Information */}
            <div className="bg-white rounded-md p-4 border border-gray-100 shadow-sm">
              <h3 className="text-base font-semibold mb-3 text-gray-700">{t('requestActions.customerInformation')}</h3>
              <div className="grid gap-2">
                {currentRequest.customer_name && (
                  <div className="flex">
                    <span className="text-sm w-20 text-gray-500">{t('booking.name')}:</span>
                    <span className="text-sm font-medium">{currentRequest.customer_name}</span>
                  </div>
                )}
                {currentRequest.customer_phone && (
                  <div className="flex">
                    <span className="text-sm w-20 text-gray-500">{t('booking.phone')}:</span>
                    <span className="text-sm font-medium">{currentRequest.customer_phone}</span>
                  </div>
                )}
                {currentRequest.customer_email && (
                  <div className="flex">
                    <span className="text-sm w-20 text-gray-500">{t('booking.email')}:</span>
                    <span className="text-sm font-medium">{currentRequest.customer_email}</span>
                  </div>
                )}
                {currentRequest.location && (
                  <div className="flex">
                    <span className="text-sm w-20 text-gray-500">{t('booking.location')}:</span>
                    <span className="text-sm font-medium">{currentRequest.location}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Service Details */}
            <div className="bg-white rounded-md p-4 border border-gray-100 shadow-sm">
              <h3 className="text-base font-semibold mb-3 text-gray-700">{t('requestActions.serviceDetails')}</h3>
              <div className="grid gap-2">
                <div className="flex">
                  <span className="text-sm w-20 text-gray-500">{t('requestActions.price')}:</span>
                  <span className="text-sm font-medium">{formatCurrency(currentRequest.services.price, userCurrency)}</span>
                </div>
                <div className="flex">
                  <span className="text-sm w-20 text-gray-500">{t('requestActions.payment')}:</span>
                  <span className={`text-sm font-medium ${currentRequest.paid ? "text-green-600" : "text-red-600"}`}>
                    {currentRequest.paid ? t('requestActions.paid') : t('requestActions.unpaid')}
                  </span>
                </div>
                {currentRequest.services.duration && (
                  <div className="flex">
                    <span className="text-sm w-20 text-gray-500">{t('requestActions.duration')}:</span>
                    <span className="text-sm font-medium">
                      {currentRequest.services.duration}
                      {currentRequest.services.duration_unit ? ` ${currentRequest.services.duration_unit}` : ''}
                    </span>
                  </div>
                )}
                {currentRequest.scheduled_at && (
                  <div className="flex">
                    <span className="text-sm w-20 text-gray-500">{t('requestActions.scheduled')}:</span>
                    <span className="text-sm font-medium">
                      {formatRequestDateTime(currentRequest.scheduled_at)}
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            
            {/* Notes (if any) */}
            {currentRequest.notes && (
              <div className="bg-white rounded-md p-4 border border-gray-100 shadow-sm">
                <h3 className="text-base font-semibold mb-2 text-gray-700">{t('requestActions.notes')}</h3>
                <p className="text-sm text-gray-600 whitespace-pre-wrap">{currentRequest.notes}</p>
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="p-4 border-t bg-gray-50">
            <div className="grid gap-3">
              <Button 
                variant="outline" 
                className="w-full justify-center"
                onClick={() => navigate('/dashboard/service-requests')}
              >
                {t('requestActions.backToRequests')}
              </Button>
              
              {!customerShareableLink && (
                <Button 
                  variant="outline" 
                  className="w-full justify-center bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200"
                  onClick={() => handleShareWithCustomer(currentRequest.id)}
                  disabled={isProcessing[currentRequest.id]}
                >
                  {isProcessing[currentRequest.id] ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Share className="h-4 w-4 mr-2" />
                  )}
                  {t('requestActions.generateCustomerLink')}
                </Button>
              )}
              
              <div className="grid grid-cols-1 gap-3 mt-2">
                {/* Mark as Paid button: show if not paid and status is accepted or completed */}
                {!currentRequest.paid && (currentRequest.status === 'accepted' || currentRequest.status === 'completed') && (
                  <Button 
                    variant="outline"
                    className="w-full justify-center"
                    onClick={() => handleDetailMarkAsPaid(currentRequest.id)}
                    disabled={isProcessing[currentRequest.id]}
                  >
                    {isProcessing[currentRequest.id] ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="h-4 w-4 mr-2" />
                    )}
                    {t('requestActions.markAsPaid')}
                  </Button>
                )}
                {/* Cancel button for accepted or pending requests */}
                {(currentRequest.status === 'accepted' || currentRequest.status === 'pending') && (
                  <Button
                    variant="outline"
                    className="w-full justify-center text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    onClick={() => { setCancelDialogOpen(true); setCancelReason(""); setCustomCancelReason(""); setCancelConfirmStep(false); }}
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    {t('requestActions.cancelRequest')}
                  </Button>
                )}
                
                {/* Mark Complete button: always show for accepted requests, even if paid */}
                {currentRequest.status === 'accepted' && (
                  <Button 
                    className="bg-blue-500 hover:bg-blue-600 text-white justify-center w-full"
                    onClick={() => handleDetailComplete(currentRequest.id)}
                    disabled={isProcessing[currentRequest.id]}
                  >
                    {isProcessing[currentRequest.id] ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    {t('requestActions.markComplete')}
                  </Button>
                )}
                
                {currentRequest.status === 'pending' && (
                  <div className="grid grid-cols-2 gap-3">
                    <Button 
                      className="bg-red-500 hover:bg-red-600 text-white justify-center"
                      onClick={() => handleDetailReject(currentRequest.id)}
                      disabled={isProcessing[currentRequest.id]}
                    >
                      {isProcessing[currentRequest.id] ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <X className="h-4 w-4 mr-2" />
                      )}
                      {t('requestActions.reject')}
                    </Button>
                    <Button 
                      className="bg-green-500 hover:bg-green-600 text-white justify-center"
                      onClick={() => handleDetailAccept(currentRequest.id)}
                      disabled={isProcessing[currentRequest.id]}
                    >
                      {isProcessing[currentRequest.id] ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-2" />
                      )}
                      {t('requestActions.accept')}
                    </Button>
                  </div>
                )}
                
                {/* Send Reminder button: show only if accepted, scheduled_at exists, scheduled_at is in the future, and service requires scheduling */}
                {currentRequest.status === 'accepted' &&
                 !!currentRequest.scheduled_at &&
                 (new Date(currentRequest.scheduled_at) > new Date()) &&
                 currentRequest.services.requires_scheduling && (
                  <Button 
                    variant="outline"
                    className="w-full justify-center"
                    onClick={() => handleDetailSendReminder(currentRequest.id)}
                    disabled={isProcessing[currentRequest.id]}
                  >
                    {isProcessing[currentRequest.id] ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Bell className="h-4 w-4 mr-2" />
                    )}
                    {t('requestActions.sendReminder')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      );
    }
    
    // Otherwise, render the regular list view
    switch (activeView) {
      case 'calendar':
        if (calendarView === 'day') {
          return <TimeSlotView 
                   date={selectedDate} 
                   requests={requests} 
                   onSendReminder={handleSendReminder}
                   onReschedule={handleReschedule}
                   onDateChange={handleDateChange}
                 />;
        } else if (calendarView === 'week') {
          const weekDates = getWeekDates();
          return (
            <div className="grid grid-cols-1 lg:grid-cols-7 gap-4">
              {weekDates.map((date) => (
                <div key={date.toISOString()} className="flex flex-col">
                  <div className="text-center font-medium mb-2">
                    <div>{formatDateWithLocale(date, 'EEEE')}</div>
                    <div>{formatDateWithLocale(date, 'MMM d')}</div>
                  </div>
                  <TimeSlotView 
                    date={date} 
                    requests={requests}
                    compact={true}
                    onSendReminder={handleSendReminder}
                    onReschedule={handleReschedule}
                  />
                </div>
              ))}
            </div>
          );
        } else {
          return (
            <div className="space-y-6">
              <div className="bg-white p-2 rounded-lg shadow border">
                <div className="mx-auto">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => date && setSelectedDate(date)}
                    locale={getDateFnsLocale()}
                    formatters={{
                      formatCaption: (date) => formatDateWithLocale(date, 'MMMM yyyy'),
                      formatWeekdayName: (date) => formatDateWithLocale(date, 'EEEEEE'),
                    }}
                    className="mx-auto"
                    classNames={{
                      month: "space-y-2 max-w-full",
                      caption: "flex justify-center pt-1 relative items-center",
                      caption_label: "text-sm font-medium",
                      nav: "space-x-1 flex items-center",
                      nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
                      nav_button_previous: "absolute left-1",
                      nav_button_next: "absolute right-1",
                      table: "w-full border-collapse space-y-1",
                      head_row: "flex w-full",
                      head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.7rem]",
                      row: "flex w-full mt-2",
                      cell: "h-7 w-7 text-center text-xs p-0 relative [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
                      day: "h-7 w-7 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md",
                      day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground ring-2 ring-primary ring-offset-1",
                      day_today: "bg-accent text-accent-foreground",
                      day_outside: "text-muted-foreground opacity-50",
                      day_disabled: "text-muted-foreground opacity-50",
                      day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
                      day_hidden: "invisible",
                    }}
                    modifiers={{
                      selected: selectedDate
                    }}
                    modifiersStyles={{
                      selected: {
                        fontWeight: "bold",
                        border: "2px solid currentColor",
                        boxShadow: "0 0 0 2px rgba(255, 20, 147, 0.3)"
                      }
                    }}
                  />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-4 text-gebeya-purple">
                  {t('navigation.scheduleFor')} {formatDateWithLocale(selectedDate, 'MMMM d, yyyy')}
                </h3>
                <TimeSlotView 
                  date={selectedDate} 
                  requests={requests} 
                  onSendReminder={handleSendReminder}
                  onReschedule={handleReschedule}
                  onDateChange={handleDateChange}
                />
              </div>
            </div>
          );
        }
      
      case 'create':
        if (!currentUserId) {
          return (
            <div className="p-6 text-center space-y-4">
              <h3 className="text-lg font-medium">{t('requestActions.userAuthRequired')}</h3>
              <p className="text-muted-foreground">
                {t('requestActions.userAuthRequiredDescription')}
              </p>
              <Button 
                variant="outline" 
                onClick={() => handleFilterChange('pending')}
              >
                {t('requestActions.goBack')}
              </Button>
            </div>
          );
        }
        
        if (isLoadingServices) {
          return (
            <div className="flex flex-col items-center justify-center p-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">{t('requestActions.loadingServices')}</p>
            </div>
          );
        }
        
        if (services.length === 0) {
          return (
            <div className="p-6 text-center space-y-4">
              <h3 className="text-lg font-medium">{t('requestActions.noServicesAvailable')}</h3>
              <p className="text-muted-foreground">
                {t('requestActions.noServicesDescription')}
              </p>
              <Button 
                variant="outline" 
                onClick={() => handleFilterChange('pending')}
              >
                {t('requestActions.goBack')}
              </Button>
            </div>
          );
        }
        
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <h3 className="text-lg font-medium mb-2">{t('requestActions.createServiceRequest')}</h3>
              <p className="text-muted-foreground text-sm">
                {t('requestActions.createServiceRequestDescription')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((service) => {
                const serviceIdx = selectedServiceIndex[service.id] || 0;
                const serviceImgs = serviceImages[service.id] || [];
                // Use the service's currency or fall back to user's currency
                const currency = service.currency || userCurrency;
                
                return (
                  <Card key={service.id} className="overflow-hidden flex flex-col h-full hover:shadow-md transition-shadow">
                    {/* Image Section - if available */}
                    {(serviceImgs.length > 0 || service.image_url) && (
                      <div className="relative aspect-square overflow-hidden bg-gray-100">
                        <img 
                          src={serviceImgs.length > 0 ? serviceImgs[serviceIdx]?.image_url : service.image_url} 
                          alt={service.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.src = 'https://placehold.co/400x400/e2e8f0/1e293b?text=No+Image';
                          }}
                        />
                      </div>
                    )}
                    
                    {/* Content Section */}
                    <CardContent className="flex-grow flex flex-col p-4">
                      <h3 className="font-medium text-lg mb-1 line-clamp-2">{service.name}</h3>
                      <p className="text-primary font-bold mb-2">
                        {formatCurrency(service.price, currency)}
                      </p>
                      
                      <div className="mt-auto pt-3">
                        <Button 
                          className="w-full text-white bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600" 
                          onClick={() => handleServiceSelect(service)}
                        >
                          {t('requestActions.createRequest')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      
      case 'upcoming':
        return (
          <RequestList 
            requests={upcomingRequests} 
            emptyMessage={t('navigation.noUpcomingRequests')} 
            showComplete={true}
            showReminder={true}
            showAutoApproved={true}
            onSendReminder={handleSendReminder}
            onComplete={handleCompleteRequest}
            onRefresh={fetchRequests}
            onMarkAsPaid={handleMarkAsPaid}
          />
        );
      
      case 'pending':
        return (
          <RequestList 
            requests={pendingRequests} 
            emptyMessage={t('navigation.noPendingRequests')} 
            showAcceptReject={true}
            showReminder={false}
            onAccept={handleAcceptRequest}
            onReject={handleRejectRequest}
            onRefresh={fetchRequests}
            onMarkAsPaid={handleMarkAsPaid}
          />
        );
      
      case 'scheduled':
        return (
          <RequestList 
            requests={acceptedRequests} 
            emptyMessage={t('navigation.noScheduledRequests')} 
            showComplete={true}
            showReminder={true}
            showAutoApproved={true}
            onSendReminder={handleSendReminder}
            onComplete={handleCompleteRequest}
            onRefresh={fetchRequests}
            onMarkAsPaid={handleMarkAsPaid}
          />
        );
      
      case 'completed':
        return (
          <RequestList 
            requests={completedRequests} 
            emptyMessage={t('navigation.noCompletedRequests')} 
            showComplete={false}
            showReminder={false}
            onRefresh={fetchRequests}
            onMarkAsPaid={handleMarkAsPaid}
          />
        );

      case 'rejected':
        return (
          <RequestList 
            requests={rejectedRequests} 
            emptyMessage={t('navigation.noRejectedRequests')} 
            showComplete={false}
            showReminder={false}
            onRefresh={fetchRequests}
            onMarkAsPaid={handleMarkAsPaid}
          />
        );
        
      case 'all':
      default:
        return (
          <RequestList 
            requests={requests} 
            emptyMessage={t('navigation.noRequestsFound')} 
            showAcceptReject={false}
            showComplete={false}
            onRefresh={fetchRequests}
            onMarkAsPaid={handleMarkAsPaid}
          />
        );
    }
  };

  return (
    <Card className="w-full border-0 shadow-none rounded-none md:rounded-lg md:border md:shadow-sm">
      <CardContent className="p-0 md:pt-6 md:px-6">
        {renderCalendarOptions()}
        <div className="p-2 md:p-0">
          {renderContent()}
        </div>
      </CardContent>

      {/* Service Checkout Dialog */}
      {selectedService && (
        <ServiceCheckoutDialog
          isOpen={isServiceCheckoutOpen}
          onClose={handleCheckoutClose}
          service={selectedService}
          onSubmitSuccess={handleCheckoutSuccess}
          contextType="admin"
        />
      )}

      {/* Cancel Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={open => { if (!open) { setCancelDialogOpen(false); setCancelConfirmStep(false); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('cancelDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('cancelDialog.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {quickReasons.map((reason) => (
                <Button
                  key={reason}
                  variant={cancelReason === reason ? "default" : "outline"}
                  className={`text-xs px-3 py-1 ${cancelReason === reason ? 'border-2 border-red-600 ring-2 ring-red-200' : ''}`}
                  onClick={() => setCancelReason(reason)}
                >
                  {reason}
                </Button>
              ))}
            </div>
            {(cancelReason === t('cancelDialog.other') || cancelReason === "") && (
              <textarea
                className="w-full border rounded p-2 text-sm"
                placeholder={t('cancelDialog.enterCustomReason')}
                value={customCancelReason}
                onChange={e => setCustomCancelReason(e.target.value)}
                rows={2}
              />
            )}
            {cancelConfirmStep && (
              <div className="text-red-700 bg-red-50 border border-red-200 rounded p-2 text-sm mt-2">
                <strong>Warning:</strong> {t('cancelDialog.warningMessage')}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelDialogOpen(false); setCancelConfirmStep(false); }} disabled={cancelProcessing}>
              {t('cancelDialog.keepRequest')}
            </Button>
            <Button 
              variant="destructive" 
              className="font-semibold bg-red-600 text-white hover:bg-red-700 border-red-600"
              onClick={handleCancelRequest}
              disabled={cancelProcessing || (!cancelReason && !customCancelReason)}
            >
              {cancelProcessing ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
              {t('cancelDialog.confirmCancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ServiceRequestsView;
